import React, { useContext, useEffect, useState } from 'react';
import { Form, Row, Radio, Typography, DatePicker } from 'antd';
import type { FormInstance } from 'antd/lib/form';
import moment from 'moment';
import type { TpAuthorizationResponse } from 'services/thirdParty';
import FormInput from 'components/FormInput';
import ButtonLink from 'components/theme_components/ButtonLink';
import { AuthContext } from 'components/Auth/Auth';
import InputCurrency from 'components/InputCurrency';
import TextArea from 'antd/es/input/TextArea';
import InputRate from 'components/InputRate';
import {
  isBeforeDate,
  isWithinDateRange,
  toDatePickerDate,
} from 'utils/utilsSFS';
import GetLabel from 'DisplayFramework/components/GetLabel';
import ConfirmModal from 'components/theme_components/ThirdParty/ConfirmModal';

export type SurActivityModalProps = {
  setVisible: (visible: boolean) => void;
  comcode: string;
  form: FormInstance;
  onDone: any;
  onCancel: any;
  dataInput: TpAuthorizationResponse;
  loading: boolean;
};

const TabOverActivity = ({
  setVisible,
  comcode,
  dataInput,
  form,
  onDone,
  loading,
  onCancel,
}: SurActivityModalProps) => {
  const { isInspect } = useContext(AuthContext);
  const [modalVisible, setModalVisible] = useState(false);


  const isToleranceActive = (tolerance: any) => {
    if (!tolerance || tolerance.status !== 'ACTIVE') {
      return false;
    }

    const now = moment();
    const startDate = moment(tolerance.startDate);
    const endDate = moment(tolerance.endDate);

    return startDate.isSameOrBefore(now) && endDate.isAfter(now);
  };


  const getActiveTolerances = (toleranceList: any[]) => {
    if (!toleranceList || !Array.isArray(toleranceList)) {
      return [];
    }

    return toleranceList.filter(isToleranceActive);
  };

  useEffect(() => {
    if (dataInput && dataInput.authDetail) {

      const activeTolerances = getActiveTolerances(dataInput.authToleranceList || []);


      form.setFieldsValue({
        authid: dataInput.authid,
        authdetailid: dataInput?.authDetail[0]?.authdetailid,
        event: 'TabOverActivity',
        flagtolerance:
          dataInput.authToleranceStatus === 'ACTIVE' ||
            dataInput.authToleranceStatus === 'SCHEDULED'
            ? true
            : false,
        tolerancetype: 'PERCENT',
      });


      if (activeTolerances && activeTolerances.length > 0) {
        const activeTolerance = activeTolerances[0];

        form.setFieldsValue({
          tolerancetype: activeTolerance.toleranceType,
          toleranceamount: activeTolerance.toleranceAmount,
          tolerancerate: activeTolerance.toleranceRate,
          startdate: toDatePickerDate(activeTolerance.startDate),
          enddate: toDatePickerDate(activeTolerance.endDate),
          comment: activeTolerance.comment,
        });
      } else {
        console.log('Aucune tolérance active trouvée, utilisation des valeurs par défaut');
        form.setFieldsValue({
          startdate: moment(),
        });
      }
    }
  }, [dataInput, form]);

  const handleValidateClick = () => {
    if (!isInspect) {
      setModalVisible(true);
    }
  };

  const disabledStartDate = (current: any) => {
    const dataInputStartDate = moment(dataInput.startdate);
    const dataInputEndDate = moment(dataInput.enddate);
    return isWithinDateRange(current, dataInputStartDate, dataInputEndDate);
  };

  const disabledEndDate = (current: any) => {
    const startDate = form.getFieldValue('startdate');
    const dataInputStartDate = moment(dataInput.startdate);
    const dataInputEndDate = moment(dataInput.enddate);
    return (
      isBeforeDate(current, startDate) ||
      isWithinDateRange(current, dataInputStartDate, dataInputEndDate)
    );
  };

  const message =
    form.getFieldValue('tolerancetype') !== 'AMOUNT'
      ? `  avec un pourcentage de ${form.getFieldValue('tolerancerate')} %`
      : `  et un montant de ${form.getFieldValue('toleranceamount')}`;

  const activeTolerances = getActiveTolerances(dataInput?.authToleranceList || []);

  return (
    <div>

      {process.env.NODE_ENV === 'development' && (
        <div style={{
          background: '#f0f8ff',
          padding: '8px',
          margin: '8px 0',
          borderRadius: '4px',
          fontSize: '12px',
          color: '#666'
        }}>
        </div>
      )}

      <FormInput
        name="flagtolerance"
        className="no-border-radius"
        labelAlign="left"
        labelCol={{ span: 16 }}
        wrapperCol={{ span: 8 }}
      >
        <Radio.Group
          className="ut-radio-group"
        >
          <Radio value> Oui </Radio>
          <Radio value={false}> Non </Radio>
        </Radio.Group>
      </FormInput>

      <Form.Item noStyle shouldUpdate>
        {({ getFieldValue }) => {
          const flagtolerance = getFieldValue('flagtolerance');
          return (
            <>
              <FormInput
                name="tolerancetype"
                className="no-border-radius"
                labelCol={{ span: 18 }}
                labelAlign="left"
                layout="vertical"
                nolabel
                style={{ marginBottom: '-14px' }}
              >
                <Radio.Group
                  disabled={!flagtolerance}
                  defaultValue={'PERCENT'}
                  className="ut-radio-group"
                >
                  <Radio value="PERCENT"> (%) </Radio>
                  <Radio value="AMOUNT"> (€) </Radio>
                </Radio.Group>
              </FormInput>

              <Form.Item noStyle shouldUpdate>
                {({ getFieldValue }) => {
                  if (getFieldValue('tolerancetype') === 'AMOUNT') {
                    return (
                      <FormInput
                        name="toleranceamount"
                        className=" mb-7 no-border-radius"
                        layout="vertical"
                        nolabel
                        rules={[
                          {
                            required: flagtolerance && getFieldValue('tolerancetype') === 'AMOUNT',
                            message: 'Montant requis'
                          }
                        ]}
                      >
                        <InputCurrency
                          disabled={!flagtolerance}
                          className=" no-border-radius ut-input-modal"
                        />
                      </FormInput>
                    );
                  } else {
                    return (
                      <FormInput
                        name="tolerancerate"
                        className=" mb-7 no-border-radius ut-task-modal-label"
                        layout="vertical"
                        nolabel
                        rules={[
                          {
                            required: flagtolerance && getFieldValue('tolerancetype') === 'PERCENT',
                            message: 'Pourcentage requis'
                          }
                        ]}
                      >
                        <InputRate
                          disabled={!flagtolerance}
                          className=" no-border-radius ut-input-modal"
                          suffix="%"
                        />
                      </FormInput>
                    );
                  }
                }}
              </Form.Item>

              <FormInput
                name="startdate"
                comcode="startdateoveractivity"
                className=" mb-7 ut-task-modal-label"
                layout="vertical"
                extra="JJ-MM-AAAA"
              >
                <DatePicker
                  disabled={!flagtolerance}
                  className=" no-border-radius ut-input-modal "
                  format={{
                    format: 'DD-MM-YYYY',
                    type: 'mask',
                  }}
                  disabledDate={disabledStartDate}
                  onChange={(date) => {
                    if (date > form.getFieldValue('enddate')) {
                      form.setFieldsValue({
                        enddate: date,
                      });
                    }
                  }}
                />
              </FormInput>
              <FormInput
                name="enddate"
                comcode="enddatedateoveractivity"
                className="form-item mb-7 ut-task-modal-label"
                layout="vertical"
                extra="JJ-MM-AAAA"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();

                      if (value.isSameOrBefore(moment(), 'day')) {
                        return Promise.reject(
                          new Error('La date de fin doit être postérieure à aujourd\'hui pour être valide.')
                        );
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <DatePicker
                  disabled={!flagtolerance}
                  className=" no-border-radius ut-input-modal"
                  format={{
                    format: 'DD-MM-YYYY',
                    type: 'mask',
                  }}
                  disabledDate={disabledEndDate}
                />
              </FormInput>
              <FormInput
                name="comment"
                className=" mb-7 ut-task-modal-label"
                layout="vertical"
              >
                <TextArea
                  disabled={!flagtolerance}
                  className=" no-border-radius"
                  rows={3}
                />
              </FormInput>
            </>
          );
        }}
      </Form.Item>

      <Row justify={'end'} style={{ marginTop: 25 }}>
        <ButtonLink
          comcode="Annuler"
          className="ut-btn-bordred"
          onClick={() => !isInspect && onCancel()}
        />
        <ButtonLink
          type="primary"
          comcode="Valider"
          htmlType="submit"
          className="ut-reset-btn"
          onClick={handleValidateClick}
          iconPosition="end"
        />
        <ConfirmModal
          padding=" 0px 80px"
          visible={modalVisible}
          status="error"
          message=""
          description={
            (() => {
              const flagtolerance = form.getFieldValue('flagtolerance');

              if (!flagtolerance) {
                return 'Souhaitez-vous désactiver la suractivité SVN ?';
              }

              const toleranceType = form.getFieldValue('tolerancetype');
              const toleranceRate = form.getFieldValue('tolerancerate') || 0;
              const toleranceAmount = form.getFieldValue('toleranceamount') || 0;

              let totalAmount = dataInput.authamount;

              if (toleranceType === 'PERCENT' && toleranceRate) {
                totalAmount = dataInput.authamount * (1 + toleranceRate / 100);
              } else if (toleranceType === 'AMOUNT' && toleranceAmount) {
                totalAmount = dataInput.authamount + toleranceAmount;
              }

              const isOverMPEA = totalAmount > dataInput.authcalculatedamount;

              return (isOverMPEA
                ? `Dépassement MPEA: (${totalAmount.toFixed(2)}€) > MPEA (${dataInput.authcalculatedamount}€). `
                : ''
              ) +
                'Souhaitez vous autoriser la suractivité SVN jusqu\'au ' +
                moment(form.getFieldValue('enddate')).format('YYYY-MM-DD') +
                message;
            })()
          }
          onConfirm={() => {
            setModalVisible(false);
            !isInspect && onDone();
          }}
          onCancel={() => !isInspect && setModalVisible(false)}
          className="modalConfirmOrCancel"
        />
      </Row>
    </div>
  );
};

export default TabOverActivity;